import type { FinalAudioNarrationSegmentDraft, UploadedWorkspaceAudio } from './types'

function createSegmentId() {
  return `tts-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`
}

export function clampTimelineStartMs(value: unknown) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) {
    return 0
  }
  return Math.max(0, Math.round(numeric))
}

export function createNarrationTimelineSegment(
  partial: Partial<FinalAudioNarrationSegmentDraft> & { audio?: UploadedWorkspaceAudio | null } = {},
): FinalAudioNarrationSegmentDraft {
  return {
    id: String(partial.id || '').trim() || createSegmentId(),
    script: String(partial.script || '').trim(),
    start_ms: clampTimelineStartMs(partial.start_ms),
    audio: partial.audio || null,
    provider: String(partial.provider || partial.audio?.provider || '').trim() || undefined,
    voice_id: String(partial.voice_id || partial.audio?.voice_id || '').trim() || undefined,
    speed: Number.isFinite(Number(partial.speed)) ? Number(partial.speed) : undefined,
  }
}

export function sortNarrationTimelineSegments(segments: FinalAudioNarrationSegmentDraft[]) {
  return [...segments].sort((left, right) => {
    if (left.start_ms === right.start_ms) {
      return left.id.localeCompare(right.id)
    }
    return left.start_ms - right.start_ms
  })
}

export function normalizeNarrationTimelineSegments(
  value: unknown,
  legacy?: {
    script?: string
    generatedAudio?: UploadedWorkspaceAudio | null
    provider?: string
    voiceId?: string
    speed?: number
  },
) {
  if (Array.isArray(value) && value.length) {
    return sortNarrationTimelineSegments(value
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const raw = item as Partial<FinalAudioNarrationSegmentDraft> & { audio?: UploadedWorkspaceAudio | null }
        return createNarrationTimelineSegment({
          id: raw.id,
          script: raw.script,
          start_ms: raw.start_ms,
          audio: raw.audio || null,
          provider: raw.provider,
          voice_id: raw.voice_id,
          speed: raw.speed,
        })
      }))
  }

  const legacyAudio = legacy?.generatedAudio || null
  if (!legacyAudio?.url) {
    return []
  }

  return [createNarrationTimelineSegment({
    script: legacy?.script || legacyAudio.name || '旁白片段',
    start_ms: 0,
    audio: legacyAudio,
    provider: legacy?.provider,
    voice_id: legacy?.voiceId,
    speed: legacy?.speed,
  })]
}

function resolveSegmentDurationMs(segment: FinalAudioNarrationSegmentDraft) {
  const seconds = Number(segment.audio?.duration_seconds)
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null
  }
  return Math.round(seconds * 1000)
}

export interface NarrationTimelineConflict {
  previous: FinalAudioNarrationSegmentDraft
  current: FinalAudioNarrationSegmentDraft
}

export function findNarrationTimelineConflict(segments: FinalAudioNarrationSegmentDraft[]): NarrationTimelineConflict | null {
  let previous: FinalAudioNarrationSegmentDraft | null = null
  for (const segment of sortNarrationTimelineSegments(segments)) {
    if (!segment.audio?.url) {
      continue
    }

    if (!previous) {
      previous = segment
      continue
    }

    const previousDurationMs = resolveSegmentDurationMs(previous)
    if (previousDurationMs !== null && segment.start_ms < previous.start_ms + previousDurationMs) {
      return { previous, current: segment }
    }

    previous = segment
  }

  return null
}

export function buildNarrationSegmentMixRequest(segments: FinalAudioNarrationSegmentDraft[]) {
  return sortNarrationTimelineSegments(segments)
    .filter((segment) => String(segment.audio?.url || '').trim())
    .map((segment) => ({
      segment_id: segment.id,
      audio_url: String(segment.audio?.url || '').trim(),
      start_ms: clampTimelineStartMs(segment.start_ms),
      duration_seconds: segment.audio?.duration_seconds ?? null,
      label: String(segment.script || segment.audio?.name || '').trim() || null,
    }))
}

export function formatTimelineTime(ms: number) {
  const totalMs = Math.max(0, clampTimelineStartMs(ms))
  const totalSeconds = Math.floor(totalMs / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  const tenths = Math.floor((totalMs % 1000) / 100)
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`
}

export function formatTimelineDuration(seconds?: number | null) {
  const numeric = Number(seconds)
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return '待识别'
  }
  return `${numeric.toFixed(1)}s`
}